/**
 * Open the organization's new chat with text already in its composer — for
 * pages outside the chat that start one ("Use in chat" on a skill).
 *
 * Files given alongside (an iManage document, for one) are staged on the
 * draft as attachments, within the composer's own count and size limits.
 *
 * Writes into the org's new-chat draft before opening it, as Home's composer
 * does (the new-thread handler's own draft, so Home and Chat never hold two
 * unsent messages for one organization). Text already in that draft is kept;
 * the new text follows it. Writing first, then opening, means the composer
 * mounts with the text: nothing has to catch it in flight.
 *
 * @module hooks/useStartChatWithText
 */
import { scopeProjectRef } from "@amoreg/client-runtime/environment";
import { useCallback, useMemo } from "react";

import { fileAttachmentMimeType, reviewFileAttachments } from "../components/chat/attachmentFiles";
import { useComposerDraftStore } from "../composerDraftStore";
import { randomUUID } from "../lib/utils";
import { useProjects } from "../state/entities";
import { usePrimaryEnvironmentId } from "../state/environments";
import { useNewThreadHandler } from "./useHandleNewThread";

/** The draft's prompt after adding `text`: kept text first, then the new text. */
export function appendDraftText(existing: string, text: string): string {
  const kept = existing.trimEnd();
  return kept.length > 0 ? `${kept}\n\n${text}` : text;
}

/** The organization's chat workspace on the primary environment; null until it is known. */
export function useOrgChatProject(organizationId: string | null) {
  const environmentId = usePrimaryEnvironmentId();
  const projects = useProjects();
  return useMemo(
    () =>
      projects.find(
        (candidate) => candidate.id === organizationId && candidate.environmentId === environmentId,
      ) ?? null,
    [environmentId, organizationId, projects],
  );
}

/** Null until the organization's chat workspace is known. */
export function useStartChatWithText(organizationId: string | null) {
  const project = useOrgChatProject(organizationId);
  const handleNewThread = useNewThreadHandler();
  const start = useCallback(
    async (text: string, files: readonly File[] = []) => {
      if (!project) return;
      const projectRef = scopeProjectRef(project.environmentId, project.id);
      const draftId = await handleNewThread(projectRef, { navigate: false });
      if (draftId !== null) {
        const store = useComposerDraftStore.getState();
        const draft = store.getComposerDraft(draftId);
        store.setPrompt(draftId, appendDraftText(draft?.prompt ?? "", text));
        if (files.length > 0) {
          const { accepted } = reviewFileAttachments({
            files: [...files],
            reservedCount: (draft?.images.length ?? 0) + (draft?.files.length ?? 0),
          });
          store.addFiles(
            draftId,
            accepted.map((file) => ({
              type: "file" as const,
              id: randomUUID(),
              name: file.name || "file",
              mimeType: fileAttachmentMimeType(file),
              sizeBytes: file.size,
              file,
            })),
          );
        }
      }
      await handleNewThread(projectRef);
    },
    [handleNewThread, project],
  );
  return project ? start : null;
}
